import { useState, useRef, useEffect } from 'react'
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from 'cmdk'
import { useVirtualizer } from '@tanstack/react-virtual'
import { useInView } from 'react-intersection-observer'
import { cn } from '$components'
import { Option } from '$components'
import { CheckIcon, LoaderIcon } from '$components'
import { useFilters } from '../hooks/use-filters'
import { ComboboxFilterDefinition } from '../types'

export type ComboboxFilterOptionProps = {
  filter: ComboboxFilterDefinition,
}

export function ComboboxFilterOption({ filter }: ComboboxFilterOptionProps) {
  const { setFilter, getFilter } = useFilters()
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const parentRef = useRef<HTMLDivElement>(null)
  const [internalSearch, setInternalSearch] = useState<string>(filter.state.search.search ?? '')
  const value = filter.state.options.find(o => String(o.id) === getFilter(filter))

  const filteredOptions = filter.state.options.filter(o => o.value.toLowerCase().includes(internalSearch.toLowerCase()))

  const isLoading = filter.state.async.isLoading || !!timeoutRef.current

  const virtualiser = useVirtualizer({
    count: filteredOptions.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => window.innerWidth < 640 ? 56 : 44,
    overscan: 5,
  })

  useEffect(() => {
    virtualiser.measure()
  }, [filteredOptions.length])

  const [ref, inView] = useInView()

  useEffect(() => {
    if (!filter.state.async || !inView || !filter.state.async.hasNextPage) {
      return
    }
    filter.state.async.fetchNextPage()
  }, [inView])

  const handleSearch = (value: string) => {
    setInternalSearch(value)
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
      timeoutRef.current = null
    }
    timeoutRef.current = setTimeout(() => {
      filter.state.search.onSearchChange(value)
      timeoutRef.current = null
    }, 300)
  }

  const handleSelect = (option: Option) => {
    if (filter.multiple) {
      let currentValues = (value ?? []) as Option[]
      const optionIndex = currentValues.findIndex(v => v.id === option.id)
      if (currentValues.length === 1 && optionIndex !== -1) {
        // @ts-expect-error needs to be null to render the placeholder
        currentValues = null
      } else if (optionIndex !== -1) {
        currentValues.splice(optionIndex, 1)
      } else {
        currentValues.push(option)
      }
      // setFilter(filter, currentValues)
    } else {
      setFilter(filter, String(option.id))
    }
  }

  const isSelected = (option: Option) => {
    if (filter.multiple) {
      // return (value as Option[] | null)?.some(v => v.id === option.id)
    }
    return option.id === (value as Option | null)?.id
  }

  return (
    <Command filter={(value, search) => {
      if (filter.state.async) {
        return 1
      }

      return value.toLowerCase().includes(search.toLowerCase()) ? 1 : 0
    }}
    >
      <div className="pb-4 mb-4 sm:mb-0 sm:px-1.5 sm:pb-1.5 border-b border-gray-100">
        <CommandInput
          value={internalSearch}
          onValueChange={handleSearch}
          placeholder="Search options"
          className="w-full p-[18px] sm:p-2.5 rounded-xl text-sm text-gray-700 focus:outline-indigo-400 bg-gray-50/50"
        />
      </div>

      <CommandList
        ref={parentRef}
        className="w-full overflow-auto max-h-[400px] sm:max-h-[300px]"
      >
        <CommandGroup
          style={{
            height: filteredOptions.length ? `${virtualiser.getTotalSize()}px` : 'auto',
          }}
          className="w-full relative"
        >
          {virtualiser.getVirtualItems().map((virtualItem) => {
            const option = filteredOptions[virtualItem.index]
            return (
              <CommandItem
                key={option.id}
                value={String(option.id)}
                onSelect={() => handleSelect(option)}
                style={{
                  height: `${virtualItem.size}px`,
                  transform: `translateY(${virtualItem.start}px)`,
                }}
                className={cn(
                  'p-4 sm:p-3 cursor-pointer focus:outline-none text-sm text-gray-700',
                  'flex items-center gap-3 w-full data-[selected=true]:bg-gray-50/50',
                  'absolute top-0 left-0 w-full rounded-xl sm:rounded-none',
                  {
                    'pr-6': !isSelected(option),
                  },
                )}
                data-selected={isSelected(option)}
              >
                { option.image && (
                  <img
                    src={option.image}
                    className="rounded-full w-8"
                  />
                )}

                <span className="truncate">
                  { option.value }
                </span>

                { isSelected(option) && (
                  <CheckIcon className="size-4 text-indigo-400 ml-auto" />
                )}
              </CommandItem>
            )
          })}
        </CommandGroup>

        {/* Intersection observer */}
        { !timeoutRef.current && (
          <div ref={ref} />
        )}

        {/* Loading indicator */}
        { isLoading && filteredOptions.length === 0 && (
          <div className="flex px-2 py-2.5 text-sm text-gray-700 justify-center">
            <LoaderIcon />
          </div>
        )}

        {/* Empty state */}
        { !isLoading && (
          <CommandEmpty className="px-2 py-3 text-sm text-gray-700 text-center">
            No results found.
          </CommandEmpty>
        )}
      </CommandList>
    </Command>
  )
}
